/**
 * COVVE behaviour layered on Horizon.
 * Reads its configuration from the data-* attributes of #covve-script, which
 * snippets/covve-head.liquid fills from the COVVE theme settings. The
 * always-drawer menu lives inline in that snippet, since it must run before
 * first paint.
 */
import {
  getScrollContainer,
  getScrollEventTarget,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';
import { prefersReducedMotion, updateAllHeaderCustomProperties } from '@theme/utilities';
import { removeTrapFocus } from '@theme/focus';

const script = document.getElementById('covve-script');
const cfg = {
  heroLogo: script?.dataset.heroLogo === 'true',
  scale: parseFloat(script?.dataset.heroLogoScale) || 3.3,
  range: parseFloat(script?.dataset.heroLogoRange) || 340,
  isHome: script?.dataset.template === 'index',
};

/* ── Hero wordmark ──────────────────────────────────────────────────────
   Over the homepage hero the text logo starts at `scale` times its resting
   size and eases down as the page scrolls — the COVVE mockup's signature
   move. Animated via font-size, never a transform: a scaled transform
   rasterises the glyphs at resting size and magnifies the bitmap (blurry).
   Desktop only (the oversized wordmark cannot fit narrower), and off when
   the visitor prefers reduced motion. The starting size is set in CSS by
   covve-head.liquid, so there is no jump before this module runs. */
const MIN_WIDTH = 860;

function heroLogo(header, signal) {
  const logo = header.querySelector('.header-logo');
  // Text logo only: an image logo has intrinsic dimensions of its own.
  if (!logo || logo.querySelector('img')) return;
  if (!header.hasAttribute('transparent')) return;
  if (prefersReducedMotion()) return;

  let queued = false;
  let last = -1;

  const paint = () => {
    queued = false;
    if (signal.aborted) return;
    if (window.innerWidth < MIN_WIDTH) {
      // Unconditionally: the resize handler resets `last`, so it can't tell us
      // whether a size is still set.
      logo.style.removeProperty('--covve-logo-size');
      last = -1;
      return;
    }
    // Horizon scrolls inside .page-wrapper from 990px up and the document
    // below that; its helper returns whichever applies right now.
    const p = Math.min(1, Math.max(0, getScrollContainer().scrollTop / cfg.range));
    if (Math.abs(p - last) <= 0.004 && (p === 1) === (last === 1)) return;
    last = p;
    // Resting size comes from covve.css, so it follows any change made there.
    const rest = parseFloat(getComputedStyle(logo).getPropertyValue('--covve-logo-rest')) || 27;
    const ease = 1 - Math.pow(1 - p, 2);
    logo.style.setProperty('--covve-logo-size', (rest * (cfg.scale - (cfg.scale - 1) * ease)).toFixed(2) + 'px');
  };

  const request = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(paint);
  };

  // The element that fires scroll events changes at 990px; re-bind there.
  let scrollTarget = null;
  const bindScroll = () => {
    scrollTarget?.removeEventListener('scroll', request);
    scrollTarget = getScrollEventTarget();
    scrollTarget.addEventListener('scroll', request, { passive: true, signal });
    last = -1;
    request();
  };

  paint();
  bindScroll();
  scrollContainerMediaQuery.addEventListener('change', bindScroll, { signal });
  window.addEventListener('resize', () => { last = -1; request(); }, { signal });
  signal.addEventListener('abort', () => {
    scrollTarget?.removeEventListener('scroll', request);
    logo.style.removeProperty('--covve-logo-size');
  });
}

/* The header section re-renders in place in the theme editor, replacing
   #header-component; tear down and set up again on the new element. */
let headerController;
function initHeader() {
  headerController?.abort();
  const header = document.getElementById('header-component');
  if (!header || !cfg.heroLogo || !cfg.isHome) return;
  headerController = new AbortController();
  heroLogo(header, headerController.signal);
}

initHeader();
document.addEventListener('shopify:section:load', (e) => {
  if (e.target.querySelector?.('#header-component')) initHeader();
});

/* ── Closable announcement bar ──────────────────────────────────────────
   covve-head.liquid provides the button (Horizon's close icon and label) as
   a <template>, only when the setting is on and outside the theme editor.
   A closed bar is remembered by a fingerprint of its text, so it stays
   closed on every page and later visits, and comes back if the text changes. */
const CLOSED_KEY = 'covve-announcements-closed';

function readClosed() {
  try {
    const list = JSON.parse(localStorage.getItem(CLOSED_KEY) || '[]');
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function fingerprint(bar) {
  const text = (bar.querySelector('.announcement-bar__slides')?.textContent || bar.textContent)
    .replace(/\s+/g, ' ')
    .trim();
  let hash = 5381;
  for (let i = 0; i < text.length; i++) hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0;
  return (hash >>> 0).toString(36);
}

function initAnnouncements() {
  const template = document.getElementById('covve-announcement-close');
  if (template) {
    const closed = readClosed();
    document.querySelectorAll('.announcement-bar').forEach((bar) => {
      const section = bar.closest('.shopify-section');
      if (!section) return;
      const id = fingerprint(bar);
      section.hidden = closed.includes(id);
      if (section.hidden || bar.querySelector(':scope > .covve-announcement-close')) return;

      const button = template.content.firstElementChild.cloneNode(true);
      button.addEventListener('click', () => {
        section.hidden = true;
        try {
          // Keep the list short; old fingerprints are for text that is gone.
          const list = readClosed().filter((x) => x !== id);
          localStorage.setItem(CLOSED_KEY, JSON.stringify([...list, id].slice(-10)));
        } catch {
          // Storage blocked: the bar still closes for this page view.
        }
        updateAllHeaderCustomProperties();
        // The focused button is gone; move focus to the first visible control
        // in the header, so keyboard users carry on from the top of the page.
        [...document.querySelectorAll('#header-component :is(a[href], button, summary)')]
          .find((el) => el.checkVisibility())
          ?.focus();
      });
      bar.append(button);
    });
  }
  document.documentElement.removeAttribute('data-covve-announcements-closed');
}

initAnnouncements();

/* ── Tabs over Product lists ────────────────────────────────────────────
   A covve-tabs section switches between the N sections that follow it,
   N being its number of tabs. Panels are ordinary Horizon sections. */
function setupTabs(tabsEl) {
  const tabs = [...tabsEl.querySelectorAll('[role="tab"]')];
  if (tabs.length < 2) return;

  const panels = [];
  let el = tabsEl.closest('.shopify-section')?.nextElementSibling;
  while (el && panels.length < tabs.length) {
    if (el.classList.contains('shopify-section')) panels.push(el);
    el = el.nextElementSibling;
  }

  tabs.forEach((tab, i) => {
    const panel = panels[i];
    if (!panel) return;
    panel.setAttribute('role', 'tabpanel');
    panel.setAttribute('aria-labelledby', tab.id);
    tab.setAttribute('aria-controls', panel.id);
  });

  const select = (i, focus) => {
    tabs.forEach((t, n) => {
      t.setAttribute('aria-selected', String(n === i));
      t.tabIndex = n === i ? 0 : -1;
    });
    panels.forEach((p, n) => { p.hidden = n !== i; });
    if (focus) tabs[i].focus();
  };

  tabsEl.covveSelect = select;
  tabsEl.covvePanels = panels;
  tabsEl.covveTabs = tabs;

  if (!tabsEl.dataset.covveBound) {
    tabsEl.dataset.covveBound = 'true';
    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => tabsEl.covveSelect(i));
      tab.addEventListener('keydown', (e) => {
        const last = tabs.length - 1;
        // Arrow keys follow reading direction, so they swap in right-to-left languages.
        const rtl = getComputedStyle(tabsEl).direction === 'rtl';
        const fwd = rtl ? 'ArrowLeft' : 'ArrowRight';
        const back = rtl ? 'ArrowRight' : 'ArrowLeft';
        const next = { [fwd]: i === last ? 0 : i + 1, [back]: i === 0 ? last : i - 1, Home: 0, End: last }[e.key];
        if (next === undefined) return;
        e.preventDefault();
        tabsEl.covveSelect(next, true);
      });
    });
  }

  const current = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
  select(current < 0 ? 0 : current);
}

// Clear every panel first: in the editor, sections can be reordered or a
// tabs section removed, and a section that is no longer a panel must not
// stay hidden. Runs synchronously, so nothing paints in between.
const initTabs = () => {
  document.querySelectorAll('.shopify-section[role="tabpanel"]').forEach((p) => {
    p.hidden = false;
    p.removeAttribute('role');
    p.removeAttribute('aria-labelledby');
  });
  document.querySelectorAll('[data-covve-tabs]').forEach(setupTabs);
};
initTabs();

// Theme editor: sections re-render in place, and selecting a hidden panel
// or a tab block should bring it into view.
document.addEventListener('shopify:section:load', initTabs);
document.addEventListener('shopify:section:reorder', initTabs);
document.addEventListener('shopify:section:unload', () => setTimeout(initTabs));
document.addEventListener('shopify:section:select', (e) => {
  document.querySelectorAll('[data-covve-tabs]').forEach((tabsEl) => {
    const i = (tabsEl.covvePanels || []).findIndex((p) => p.id === `shopify-section-${e.detail.sectionId}`);
    if (i >= 0) tabsEl.covveSelect(i);
  });
});
document.addEventListener('shopify:block:select', (e) => {
  const tab = e.target.closest?.('[role="tab"]');
  const tabsEl = tab?.closest('[data-covve-tabs]');
  if (tabsEl?.covveTabs) tabsEl.covveSelect(tabsEl.covveTabs.indexOf(tab));
});

/* ── Menu drawer: two-column switching and hover-to-open ────────────────
   From 990px covve.css lays Men / Women out as a second column beside the
   top level. Horizon opens a submenu without closing a sibling (its
   submenus normally cover the top level, so that never mattered); here a
   newly opened column replaces the open one, and clicking the open item
   again closes it. With a mouse, resting on a top-level item opens its
   column (or closes the open one for plain links) after a short pause, so
   a pointer passing over the list doesn't flicker columns open. */
const megaMenuQuery = window.matchMedia('(min-width: 990px)');
const finePointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
const HOVER_DELAY = 160;
// Horizon re-renders the header's attributes in place, so remember drawers in memory, not in a data attribute.
const menuDrawers = new WeakSet();

const topItemsOf = (drawer) => [...drawer.querySelectorAll('.menu-drawer__navigation > .menu-drawer__menu > .menu-drawer__list-item')];
const topDetailsOf = (drawer) => topItemsOf(drawer).map((li) => li.querySelector(':scope > details')).filter(Boolean);
const closeQuietly = (details) => {
  details.classList.remove('menu-open');
  details.removeAttribute('open');
  details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
};

/* Horizon delegates its drawer clicks above the drawer, so listen on the window in the capture
   phase: this runs first, and can take over a click entirely (stopImmediatePropagation) when the
   open item is clicked again, before Horizon schedules a re-open. */
window.addEventListener(
  'click',
  (event) => {
    if (!megaMenuQuery.matches || !(event.target instanceof Element)) return;
    const summary = event.target.closest('header-drawer summary');
    const details = summary?.parentElement;
    const drawer = summary?.closest('header-drawer');
    if (!(details instanceof HTMLDetailsElement) || !drawer || !topDetailsOf(drawer).includes(details)) return;

    if (details.classList.contains('menu-open')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      drawer.back(event);
      return;
    }
    const open = topDetailsOf(drawer).filter((other) => other !== details && other.classList.contains('menu-open'));
    if (open.length) {
      open.forEach(closeQuietly);
      // The closed column held Horizon's focus trap (it pulls focus back on mousedown, before this
      // click). Release it and put focus on the chosen item, so focus and Escape follow the new column.
      removeTrapFocus();
      summary.focus({ preventScroll: true });
    }
  },
  { capture: true }
);

/* Back button in a submenu: return focus to the item that opened it (Horizon leaves focus on
   <body>), so keyboard users carry on from where they were. All widths. */
window.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const back = event.target.closest('header-drawer .menu-drawer__back-button');
  const owner = back?.closest('.menu-drawer__submenu')?.parentElement;
  if (owner instanceof HTMLDetailsElement) setTimeout(() => owner.querySelector(':scope > summary')?.focus({ preventScroll: true }));
});

/* Escape in the drawer (Horizon behaviour fixed here, at all widths):
   - inside an open submenu, focus returns to the item that opened it once the submenu closes;
   - on a top-level item whose submenu is closed, the whole drawer closes. Horizon resolves Escape
     to the nearest <details>, which for that item is its own closed submenu, so a second Escape
     after closing a submenu otherwise did nothing. */
window.addEventListener(
  'keyup',
  (event) => {
    if (event.key !== 'Escape' || !(event.target instanceof Element)) return;

    // Escape inside an open submenu: Horizon closes it but leaves focus on a now-hidden link
    // (its trapFocus ends by focusing the <details>, which can't take focus). Return focus to
    // the item that opened the submenu, once Horizon has handled the key.
    const column = event.target.closest('header-drawer .menu-drawer__submenu');
    if (column) {
      const owner = column.parentElement;
      const ownerDrawer = column.closest('header-drawer');
      if (owner instanceof HTMLDetailsElement && ownerDrawer && topDetailsOf(ownerDrawer).includes(owner) && owner.classList.contains('menu-open')) {
        setTimeout(() => owner.querySelector(':scope > summary')?.focus({ preventScroll: true }));
      }
      return;
    }

    const summary = event.target.closest('header-drawer summary');
    const details = summary?.parentElement;
    const drawer = summary?.closest('header-drawer');
    if (!(details instanceof HTMLDetailsElement) || !drawer?.isOpen || !topDetailsOf(drawer).includes(details)) return;
    if (details.classList.contains('menu-open')) return;
    event.stopImmediatePropagation();
    drawer.close();
  },
  { capture: true }
);

function initMenuDrawer() {
  const drawer = document.querySelector('header-drawer');
  if (!drawer || menuDrawers.has(drawer)) return;
  menuDrawers.add(drawer);

  let hoverTimer;
  let hoverItem = null;
  drawer.addEventListener('pointerover', (event) => {
    if (!megaMenuQuery.matches || !finePointerQuery.matches || !drawer.isOpen) return;
    if (!(event.target instanceof Element)) return;
    // Only the top-level rows themselves count: movement inside a column (or over one that is
    // animating closed) must never open or close anything.
    if (event.target.closest('.menu-drawer__submenu')) return;
    const item = event.target.closest('.menu-drawer__list-item');
    if (!item || !topItemsOf(drawer).includes(item) || item === hoverItem) return;
    hoverItem = item;
    clearTimeout(hoverTimer);
    hoverTimer = setTimeout(() => {
      const details = item.querySelector(':scope > details');
      if (details) {
        if (!details.classList.contains('menu-open')) details.querySelector('summary')?.click();
      } else {
        const open = topDetailsOf(drawer).find((d) => d.classList.contains('menu-open'));
        if (open) drawer.back({ target: open.querySelector('summary') });
      }
    }, HOVER_DELAY);
  });
  drawer.addEventListener('pointerleave', () => {
    clearTimeout(hoverTimer);
    hoverItem = null;
  });
}

initMenuDrawer();
document.addEventListener('shopify:section:load', (e) => {
  if (e.target.querySelector?.('header-drawer')) initMenuDrawer();
});
